import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
  Icon,
  Heading,
  Box,
  Flex,
  Checkbox,
  FormControl,
  IconButton,
  Spinner,
  Center,
  ButtonGroup,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useState } from "react";
import {
  FaArrowLeft,
  FaChevronRight,
  FaCopy,
  FaInfoCircle,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { WarehouseTypeDetails } from "../../../../../common/types/common";
import ConnectionDetailsForm from "../../components/ConnectionDetailsForm";
import {
  resetFlags,
  selectConnection,
  setSelectedSchema,
  schemaSet,
  wizardNextStep,
  wizardPrevStep,
  whiteListedIps,
  wizardSetStep,
} from "../../connectionSlice";
import { copyToClipboard } from "../../../../../common/helper/commonHelper";
import DropdownWithSearch from "../../../../../components/DropdownWithSearch";
import IButton, { BUTTON } from "../../../../../components/IButton";
import ITitle from "../../../../../components/ITitle";
import { SOURCES } from "../../../../../common/types/unifiedMapping";
import CopyDatabaseName from "./CopyDatabaseName";
import BigqueyAccessRequestDetails from "./BigqueyAccessRequestDetails";

function IpAddressRow({ ip }: { ip: string }) {
  return (
    <Flex justifyContent="space-between" alignItems="center">
      <Text fontWeight="bold" color="orange.400">
        {ip}
      </Text>
      <IButton
        onClick={() => copyToClipboard(ip)}
        variant="ghost"
        color="gray.500"
        size={"xs"}
        name="copy-button"
        leftIcon={<Icon as={FaCopy} />}
      >
        Copy
      </IButton>
    </Flex>
  );
}

function SelectSchemaForm({
  serviceDetails,
  setSchema,
  schema,
  setupConnection,
  closeModal,
}: {
  serviceDetails: WarehouseTypeDetails;
  setSchema: React.Dispatch<React.SetStateAction<string>>;
  schema: string;
  setupConnection: () => void;
  closeModal: () => void;
}) {
  const { schemaList, updatingConnection } = useSelector(selectConnection);

  const dispatch = useDispatch();

  const goToPrevStep = useCallback(() => {
    if (serviceDetails?.client_id === SOURCES.BIGQUERY) {
      dispatch(wizardPrevStep());
    } else {
      dispatch(wizardSetStep(2));
    }
  }, [dispatch, serviceDetails?.client_id]);

  return (
    <>
      <ModalHeader pl={2} color="blue.700" fontSize="sm">
        <IconButton
          aria-label="Go Back"
          variant="ghost"
          name="return-button"
          size={"sm"}
          icon={<Icon as={FaArrowLeft} />}
          mr={2}
          onClick={goToPrevStep}
        />
        {serviceDetails?.name}
      </ModalHeader>
      <ModalCloseButton />
      <ModalBody px={12} minH="180px">
        <ITitle
          fontSize={12}
          fontWeight="bold"
          title="Connected Successfully"
        />
        <ITitle
          fontSize={16}
          fontWeight="bold"
          title=" Let’s setup the sync,"
        />

        <FormControl id="schema" mt={8}>
          <Text fontSize={12}>
            {`Choose a ${
              serviceDetails.client_id === SOURCES.BIGQUERY
                ? "dataset"
                : "schema"
            } to setup connection sync`}
          </Text>
          <DropdownWithSearch
            options={schemaList.map((schema) => {
              return {
                label: schema,
                value: schema,
              };
            })}
            onChange={(option) => setSchema(option?.value ?? "")}
          />
        </FormControl>
      </ModalBody>
      <ModalFooter justifyContent="flex-start" px={12} py={6}>
        <IButton
          variant="ghost"
          onClick={closeModal}
          name="cancel-button"
          mr={2}
        >
          Cancel
        </IButton>
        <IButton
          variant={BUTTON.PRIMARY}
          isLoading={updatingConnection}
          loadingText="Setting Up Connection"
          isDisabled={!schema || updatingConnection}
          name="setup-button"
          mr={3}
          px={5}
          rightIcon={<Icon as={FaChevronRight} />}
          onClick={setupConnection}
        >
          Setup connection
        </IButton>
      </ModalFooter>
    </>
  );
}

function StaticIpForm({
  whiteListedIP,
  toggleWhitelistCheckbox,
  closeModal,
}: {
  whiteListedIP: boolean;
  toggleWhitelistCheckbox: () => void;
  closeModal: () => void;
}) {
  const dispatch = useDispatch();

  const { whiteListIps, fetchingWhiteListIps } = useSelector(selectConnection);

  useEffect(() => {
    if (!fetchingWhiteListIps && !whiteListIps) {
      dispatch(whiteListedIps());
    }
  }, [dispatch, fetchingWhiteListIps, whiteListIps]);

  return (
    <>
      <ModalHeader color="blue.700" fontSize="sm">
        <Icon as={FaInfoCircle} mr={2} />
        <ITitle title="Important prerequisite" />
      </ModalHeader>
      <ModalCloseButton />
      <ModalBody pt={8} px={10}>
        <Heading size={"xs"}>Required Permissions</Heading>
        <Text fontSize={"xs"} mt={2}>
          Inflection requires specific permissions to sync data. You can find
          detailed Redshift instructions
        </Text>

        <Heading size={"xs"} mt={5}>
          Allow these static IPs to establish connection
        </Heading>

        <Text fontSize={"xs"} mt={2}>
          Allow the following IPs in your AWS dashboard to connect
        </Text>
        <Box my={5}>
          {whiteListIps && whiteListIps.length
            ? whiteListIps.map((ip) => {
                return <IpAddressRow ip={ip} />;
              })
            : fetchingWhiteListIps && (
                <Center h="100px">
                  <Spinner />
                </Center>
              )}
        </Box>

        <Checkbox
          isChecked={whiteListedIP}
          onChange={toggleWhitelistCheckbox}
          mt={12}
        >
          <Text fontWeight="semibold" fontSize="sm">
            I've Whitelisted the IPs
          </Text>
        </Checkbox>
      </ModalBody>
      <ModalFooter justifyContent="flex-end" px={6} py={6}>
        <ButtonGroup>
          <IButton variant="ghost" onClick={closeModal} name="cancel-button">
            Cancel
          </IButton>
          <IButton
            variant={BUTTON.PRIMARY}
            isDisabled={!whiteListedIP}
            name="continue-button"
            mr={3}
            w={150}
            rightIcon={<Icon as={FaChevronRight} />}
            onClick={() => dispatch(wizardNextStep())}
          >
            Continue
          </IButton>
        </ButtonGroup>
      </ModalFooter>
    </>
  );
}

export default function ConnectionDetailsModal({
  isOpen,
  onClose,
  goToNext,
  serviceDetails,
}: {
  isOpen: boolean;
  onClose: () => void;
  goToNext: () => void;
  serviceDetails: WarehouseTypeDetails;
}) {
  const dispatch = useDispatch();

  const [schema, setSchema] = useState("");

  const [whiteListedIP, setWhiteListedIP] = useState(false);
  const toggleWhitelistCheckbox = () => setWhiteListedIP(!whiteListedIP);

  const {
    createCredsWizardStep,
    creatingNewConnection,
    createdNewConnection,
    connectionDetails,
    addedSchema,
  } = useSelector(selectConnection);

  useEffect(() => {
    return () => {
      dispatch(resetFlags());
    };
  }, [dispatch]);

  const closeModal = useCallback(() => {
    dispatch(resetFlags());
    onClose();
  }, [dispatch, onClose]);

  useEffect(() => {
    if (addedSchema) {
      dispatch(setSelectedSchema(schema));
      closeModal();
      goToNext();
    }
  }, [addedSchema, goToNext, dispatch, schema, closeModal]);

  useEffect(() => {
    if (createdNewConnection) {
      dispatch(wizardNextStep());
    }
  }, [createdNewConnection, dispatch]);

  const setupConnection = () => {
    if (connectionDetails && connectionDetails.id) {
      dispatch(schemaSet({ id: connectionDetails.id, schemaName: schema }));
    }
  };

  function stepSwitch(step: number) {
    function goBackToStepOne() {
      if (
        serviceDetails.client_id !== SOURCES.REDSHIFT &&
        serviceDetails.client_id !== SOURCES.BIGQUERY
      ) {
        closeModal();
      } else {
        dispatch(wizardPrevStep());
      }
    }

    switch (step) {
      case 1:
        if (
          serviceDetails &&
          (serviceDetails.client_id === SOURCES.REDSHIFT ||
            serviceDetails.client_id === SOURCES.BIGQUERY)
        ) {
          return serviceDetails.client_id === SOURCES.REDSHIFT ? (
            <StaticIpForm
              whiteListedIP={whiteListedIP}
              toggleWhitelistCheckbox={toggleWhitelistCheckbox}
              closeModal={closeModal}
            />
          ) : (
            <CopyDatabaseName
              onClose={closeModal}
              goToNext={() => dispatch(wizardNextStep())}
            />
          );
        }

        dispatch(wizardNextStep());
        return <></>;

      case 2:
        return (
          <ConnectionDetailsForm
            serviceDetails={serviceDetails}
            closeModal={closeModal}
            loading={creatingNewConnection}
            goToPrevStep={goBackToStepOne}
          />
        );

      case 3:
        if (serviceDetails && serviceDetails.client_id === SOURCES.BIGQUERY) {
          return <BigqueyAccessRequestDetails onClose={closeModal} />;
        }
        dispatch(wizardNextStep());
        return <></>;
      case 4:
        return (
          <SelectSchemaForm
            serviceDetails={serviceDetails}
            setSchema={setSchema}
            schema={schema}
            setupConnection={setupConnection}
            closeModal={closeModal}
          />
        );
    }
  }

  return (
    <Modal
      size={"md"}
      isOpen={isOpen}
      onClose={closeModal}
      isCentered
      autoFocus={false}
    >
      <ModalOverlay />
      <ModalContent>{stepSwitch(createCredsWizardStep)}</ModalContent>
    </Modal>
  );
}
